/**
 * SessionDebugStrip — brief-3 §8.3.
 *
 * Admin-only collapsed panel surfacing the data the team needs to debug
 * a Phase 2 session in flight: the current session id, last_active_at,
 * resumed flag, kickoff status, planner_reason + chosen card, and the
 * live list of `session_card_events` rows for the session.
 *
 * Mounted inside `Interview.tsx` behind a `super_admin` role check (the
 * parent decides whether to render — this component itself does not
 * gate). Polls `/admin/sessions/:id/card-events` every 5s while open;
 * collapsed it does not poll, so the cost of leaving it mounted is zero
 * until an admin expands it.
 *
 * Reads `useAuthStore` for the role check upstream; this component is
 * presentational once mounted.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Bug } from 'lucide-react';
import { apiClient } from '@/services/api-client';
import { cn } from '@/lib/utils';

interface CardEvent {
  id: string;
  cardId: string;
  event: string;
  reason: string | null;
  source: string;
  createdAt: string;
}

interface CardEventsResponse {
  session: {
    id: string;
    userId: string;
    lastActiveAt: string;
    kickoffStatus: string;
    plannedCardId: string | null;
    plannerReason: string | null;
  };
  events: CardEvent[];
}

interface SessionDebugStripProps {
  sessionId: string;
  /** From the /interviews/start response. */
  resumed: boolean;
  /** Top-of-funnel kickoff status from /interviews/start; can change via SSE. */
  kickoffStatus: 'pending' | 'ready' | null;
}

const debugStripQueryKey = (sessionId: string) =>
  ['admin', 'sessions', sessionId, 'card-events'] as const;

export function SessionDebugStrip({ sessionId, resumed, kickoffStatus }: SessionDebugStripProps) {
  const [open, setOpen] = useState(false);

  // Only query while expanded — collapsed strip is a no-op.
  const { data, isLoading, error } = useQuery<CardEventsResponse>({
    queryKey: debugStripQueryKey(sessionId),
    queryFn: async () => {
      const res = await apiClient.get<CardEventsResponse>(
        `/api/v1/admin/sessions/${sessionId}/card-events`
      );
      if (!res.success || !res.data) {
        throw new Error(res.error?.message ?? 'Failed to load debug data');
      }
      return res.data as unknown as CardEventsResponse;
    },
    enabled: open && !!sessionId,
    refetchInterval: open ? 5_000 : false,
    staleTime: 0,
  });

  return (
    <div
      data-testid="session-debug-strip"
      className="mb-2 rounded-md border border-orb-teal/30 bg-orb-cream/40 text-xs text-foreground"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-orb-cream/60"
        aria-expanded={open}
      >
        <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-orb-teal">
          <Bug className="h-3 w-3" />
          debug · session {sessionId.slice(0, 8)}
          {resumed && <span className="rounded bg-orb-teal/20 px-1.5 py-0.5">resumed</span>}
          <span className="rounded bg-muted/60 px-1.5 py-0.5">
            kickoff: {kickoffStatus ?? 'n/a'}
          </span>
        </span>
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>

      {open && (
        <div className="space-y-2 border-t border-orb-teal/20 px-3 py-2 font-mono text-[11px]">
          {isLoading && <div className="text-muted-foreground">loading…</div>}
          {error && (
            <div role="alert" className="text-destructive">
              {(error as Error).message}
            </div>
          )}
          {data && (
            <>
              <DebugRow label="session_id" value={data.session.id} />
              <DebugRow
                label="last_active_at"
                value={new Date(data.session.lastActiveAt).toISOString()}
              />
              <DebugRow label="kickoff_status" value={data.session.kickoffStatus} />
              <DebugRow label="planner_reason" value={data.session.plannerReason ?? '—'} />
              <DebugRow label="planned_card" value={data.session.plannedCardId ?? '—'} />

              <div className="pt-2">
                <div className="text-[10px] uppercase tracking-wider text-orb-teal">
                  session_card_events ({data.events.length})
                </div>
                {data.events.length === 0 ? (
                  <div className="mt-1 text-muted-foreground">no events yet</div>
                ) : (
                  <ul className="mt-1 space-y-0.5">
                    {data.events.map((e) => (
                      <li
                        key={e.id}
                        className={cn(
                          'flex flex-wrap gap-x-2 gap-y-0.5',
                          e.event === 'skipped_in_session' && 'text-orb-gold',
                          e.event === 'engaged' && 'text-orb-teal',
                          e.event === 'proposed' && 'text-muted-foreground'
                        )}
                      >
                        <span className="tabular-nums text-muted-foreground">
                          {new Date(e.createdAt).toISOString().slice(11, 19)}
                        </span>
                        <span className="font-semibold">{e.event}</span>
                        <span className="text-muted-foreground">card={e.cardId.slice(0, 8)}</span>
                        <span className="text-muted-foreground">src={e.source}</span>
                        {e.reason && (
                          <span className="text-muted-foreground">reason={e.reason}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DebugRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <span className="w-32 shrink-0 text-muted-foreground">{label}</span>
      <span className="break-all">{value}</span>
    </div>
  );
}
